import {
	DCarousel,
	DCarouselInternalData,
	DCarouselRef,
	DCarouselResponsiveSettings
} from "@dfa/ui-platform";
import { useCallback, useEffect, useRef, useState } from "react";
import { Card } from "./types/card-carousel-types";
import CarouselNavigation from "./CarouselNavigation";
import FeaturedTextBox from "./FeaturedTextBox";
import ImageCard from "./cards/ImageCard";
import { SCREEN_WIDTHS } from "components/utility/constants/screen_widths";

export type ImageCardProps = {
	title: string;
	backgroundImage: string;
	type: string;
	linkText: string;
	link: string;
};

interface ImageCardCarouselProps {
	data: {
		contentImageCardContainer: {
			accentBarColor: string;
			eyebrow: string;
			headline?: string;
			description: string;
			ctaLinkText: string;
			ctaLinkUrl: string;
		};
		cards: Card[];
	};
}

const breakpointSettings: DCarouselResponsiveSettings = [
	{
		breakpoint: 1024,
		settings: {
			cardsPerSection: 1,
			cardsToScroll: 1
		}
	},
	{
		breakpoint: 768,
		settings: {
			cardsPerSection: 1,
			cardsToScroll: 1,
			autoplaySettings: {
				autoplayDirection: "forwards",
				intervalDuration: 3000
			},
			wraps: true
		}
	}
];

const ImageCardCarousel = ({ data }: ImageCardCarouselProps) => {
	const carouselRef = useRef<DCarouselRef>(null);

	const [internalCarouselData, setInternalCarouselData] = useState<DCarouselInternalData>();
	const { accentBarColor, eyebrow, headline, description, ctaLinkText, ctaLinkUrl } =
		data.contentImageCardContainer;
	const cardData = data.cards;
	const { LG, SM, XS } = SCREEN_WIDTHS;

	const initImageCardSize = useCallback(() => {
		let imageCardSize = 568;
		if (window.innerWidth < LG && window.innerWidth > SM) {
			imageCardSize = 429;
		} else if (window.innerWidth <= SM && window.innerWidth > XS) {
			imageCardSize = 320;
		} else if (window.innerWidth <= XS) {
			imageCardSize = 328;
		}
		return imageCardSize;
	}, [LG, SM, XS]);
	const [imageCardSize, setImageCardSize] = useState(initImageCardSize());

	useEffect(() => {
		const screenSize = () => {
			if (cardData.length > 0) {
				setImageCardSize(initImageCardSize());
			}
		};
		window.addEventListener("resize", screenSize);
		screenSize();
		return () => {
			window.removeEventListener("resize", screenSize);
		};
	}, [cardData.length, LG, SM, XS, initImageCardSize]);

	return (
		<div className="card-carousel-container image-container">
			<div className="card-text-box">
				{"contentImageCardContainer" in data && (
					<FeaturedTextBox
						accentBarColor={accentBarColor}
						eyebrow={eyebrow}
						headline={headline}
						description={description}
						linkText={ctaLinkText}
						linkUrl={ctaLinkUrl}
					/>
				)}
			</div>
			<div className="image-card-container">
				<DCarousel
					ref={carouselRef}
					setCarouselInternal={setInternalCarouselData}
					responsiveSettings={breakpointSettings}
					settings={{
						carouselClassname: "image-cards",
						cardsPerSection: 1,
						cardsToScroll: 1,
						draggable: true,
						gapBetweenCards: "2rem",
						prettyWrapping: true
					}}
				>
					{data.cards?.map((card, i) => {
						return <ImageCard card={card} i={i} key={card.title} cardSize={imageCardSize} />;
					})}
				</DCarousel>
				{internalCarouselData && internalCarouselData.totalSections > 0 ? (
					<CarouselNavigation
						carouselRef={carouselRef}
						clickNumber={internalCarouselData.clickNumber}
						clicksToEnd={internalCarouselData.clicksToEnd}
						currentSection={internalCarouselData.currentSection}
						totalSections={internalCarouselData.totalSections}
					/>
				) : null}
			</div>
		</div>
	);
};

export default ImageCardCarousel;
